import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { formatRelativeTime } from "@/lib/dateUtils";
import {
  Loader2,
  Inbox,
  DollarSign,
  UserMinus,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";

// Top-of-dashboard summary of "what needs my attention right now." Each pill
// is clickable: entries → scrolls to All entries with status=submitted filter
// pre-applied; commissions → scrolls to commissions card; stale freelancers
// → opens the Team page (already shows last_seen).
//
// onEntriesPending: parent provides a callback to set the entries page
// status filter to "submitted" so the click is one-action ("show me what to
// review now").
export default function AdminAttentionStrip({ onEntriesPending }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dashboard/needs-attention");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Re-poll roughly every 60s while the tab is open so a freelancer hitting
  // submit shows up here without an admin reload. Cheap call.
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 mb-4 flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="animate-spin" size={14} />
        Loading…
      </div>
    );
  }
  if (!data) return null;

  const { submitted_entries, submitted_commissions, stale_freelancer_count, stale_threshold_days } = data;
  const total = submitted_entries + submitted_commissions + stale_freelancer_count;

  if (total === 0) {
    return (
      <div
        className="rounded-2xl border px-4 py-3 mb-4 flex items-center gap-2 text-sm"
        style={{
          background: `${BRAND.teal}08`,
          borderColor: `${BRAND.teal}40`,
          color: BRAND.tealDark,
        }}
        data-testid="attention-strip-empty"
      >
        <CheckCircle2 size={16} />
        <span className="font-medium">All clear</span>
        <span className="text-slate-500">— nothing needs your review.</span>
      </div>
    );
  }

  const scrollToCommissions = () => {
    const el = document.querySelector('[data-testid="admin-commissions-card"]');
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 mb-4 flex flex-wrap items-center gap-2"
      data-testid="attention-strip"
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">
        Needs review
      </span>
      {submitted_entries > 0 && (
        <Pill
          Icon={Inbox}
          color="#b45309"
          bg="#f59e0b22"
          label={`${submitted_entries} ${submitted_entries === 1 ? "entry" : "entries"} awaiting review`}
          onClick={() => {
            onEntriesPending?.();
            const el = document.querySelector('[data-testid="admin-status-filter"]');
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
          testId="attention-entries-pill"
        />
      )}
      {submitted_commissions > 0 && (
        <Pill
          Icon={DollarSign}
          color={BRAND.teal}
          bg={`${BRAND.teal}18`}
          label={`${submitted_commissions} commission${submitted_commissions === 1 ? "" : "s"} pending`}
          onClick={scrollToCommissions}
          testId="attention-commissions-pill"
        />
      )}
      {stale_freelancer_count > 0 && (
        <Pill
          Icon={UserMinus}
          color="#475569"
          bg="#94a3b820"
          label={`${stale_freelancer_count} freelancer${stale_freelancer_count === 1 ? "" : "s"} inactive ${stale_threshold_days}+ days`}
          onClick={() => navigate("/admin/team")}
          testId="attention-stale-pill"
          tooltip={
            data.stale_freelancers
              ?.slice(0, 5)
              .map((u) =>
                `${u.name}${u.last_login_at ? ` · last seen ${formatRelativeTime(u.last_login_at)}` : " · never"}`
              )
              .join("\n") || undefined
          }
        />
      )}
    </div>
  );
}

function Pill({ Icon, color, bg, label, onClick, testId, tooltip }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={tooltip}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition hover:opacity-80"
      style={{ background: bg, color }}
    >
      <Icon size={12} />
      {label}
      <ChevronRight size={12} className="opacity-70" />
    </button>
  );
}
